import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

import ReactSeatInfo from '@stubhub/react-seat-info';

import {arrayToRangeWithSort} from '../../../../helpers/data-array-to-range';
import ValueScore from '../react-value-score';

// CSS
import './index.scss';

export const Section = ({section = '', isEventGA = false, className = ''}) => {
  return (
    <div className={`SectionRowSeat__sectionTitle ${className}`}>
      {isEventGA ? <FormattedMessage id="SectionRowSeatGA.section" defaultMessage="General Admission" /> : section}
    </div>
  );
};

Section.propTypes = {
  section: PropTypes.string,

  /**
   * If is general admission
   */
  isEventGA: PropTypes.bool,

  /**
   * The Class Name used to override the Styling for the component.
   *
   */
  className: PropTypes.string,
};

export const Row = ({row, className = ''}) => {
  return (
    <span className={`SectionRowSeat__row ${className}`}>
      <FormattedMessage id="SectionRowSeat.row" defaultMessage="Row" />
      &nbsp;
      {row}
    </span>
  );
};

Row.propTypes = {
  row: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * The Class Name used to override the Styling for the component.
   *
   */
  className: PropTypes.string,
};

export const Seats = ({seats, className = ''}) => {
  return (
    <span className={`SectionRowSeat__seats ${className}`}>
      <FormattedMessage id="SectionRowSeat.seats" defaultMessage="Seats" />
      &nbsp;
      {seats}
    </span>
  );
};

Seats.propTypes = {
  seats: PropTypes.array,

  /**
   * The Class Name used to override the Styling for the component.
   *
   */
  className: PropTypes.string,
};

export const isRowValid = (row) => {
  /*
   * TODO: CONVERT-288: Needs to be handled in sell flow and inventory, not on front-end.
   * Please remove after coordinating proper implementation
   */
  const unspecifiedRows = new Set([
    '.',
    '..',
    '...',
    '-',
    '--',
    '---',
    'N/A',
    'n/a',
    'na',
    'NA',
    '',
    null,
    undefined,
    false,
    0,
    'GA',
  ]);

  return !unspecifiedRows.has(row);
};

const productsToSeatArray = (products = []) => {
  return products.reduce((acc, product) => {
    const {seat} = product;
    if (!isNaN(Number(seat))) {
      acc.push(seat);
    }

    return acc;
  }, []);
};

const seatsToString = (products = []) => {
  const seatsArray = productsToSeatArray(products);
  if (seatsArray.length === 0) {
    return null;
  }

  return arrayToRangeWithSort(seatsArray);
};

const isGeneralAdmissionSection = (products = []) => {
  if (products.length && products[0]) {
    return products[0].ga || false;
  }

  return false;
};

const ValueScoreBYO = ({className, valuePercentage}) => (
  <div className={className}>
    <ValueScore valuePercentage={valuePercentage} />
  </div>
);

ValueScoreBYO.propTypes = {
  /**
   * The Class Name used to override the Styling for the component.
   *
   */
  className: PropTypes.string,
  valuePercentage: PropTypes.number,
};

/**
 * @param section
 * @param row
 * @param seats
 */

const SectionRowSeat = ({
  section,
  row,
  products = [],
  quantity,
  valuePercentage,
  showTicketRecoInd,
  partner,
  showSeatInfo,
}) => {
  const seats = seatsToString(products);
  const isGASection = isGeneralAdmissionSection(products);
  const showPartnerLogoAndTicketValue = partner && showTicketRecoInd && valuePercentage;

  const rowSeatContainerClassName = showPartnerLogoAndTicketValue
    ? 'SectionRowSeat__rowSeatContainer ticketValuePartner'
    : 'SectionRowSeat__rowSeatContainer';

  return (
    <div className="SectionRowSeat">
      <div className="SectionRowSeat__sectionTitle">
        {showTicketRecoInd && valuePercentage && (
          <ValueScoreBYO
            className="RoyalBuildYourOrder__body_section_container_value"
            valuePercentage={valuePercentage}
          />
        )}
        {section}
      </div>
      <div className={rowSeatContainerClassName}>
        {isRowValid(row) && (
          <span className="SectionRowSeat__row">
            <FormattedMessage id="SectionRowSeat.row" defaultMessage="Row" />
            &nbsp;
            {row}
          </span>
        )}
        {seats && !isGASection && (
          <>
            {isRowValid(row) && <span className="SectionRowSeat__separator_dot">·</span>}
            <FormattedMessage id="SectionRowSeat.seats" defaultMessage="Seats" />
            &nbsp;
            <div className="SectionRowSeat__seats">{seats}</div>
          </>
        )}
      </div>
      <ReactSeatInfo showInfo={showSeatInfo} products={products} quantity={quantity} isInformed={!!seats} />
    </div>
  );
};

SectionRowSeat.propTypes = {
  section: PropTypes.string,
  row: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  products: PropTypes.array,
  quantity: PropTypes.number.isRequired,
  valuePercentage: PropTypes.number,
  showTicketRecoInd: PropTypes.bool.isRequired,

  /** whether to show the seat info */
  showSeatInfo: PropTypes.bool,
  partner: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default SectionRowSeat;
